import { computed } from 'vue';
import type { ChartOption, ChartData, SeriesData } from '@/common/utils/types';
import { useEvChartStyle } from '@/common/utils/chartUtils';
import { getCSSVariable } from '@/common/utils/browser';
import dayjs from 'dayjs';
import { TrendChartTickInfo } from './tendChartCell.type';
import { getTrendChartLastValue } from './trendChartCell.util';

export interface Props {
  data: TrendChartTickInfo | null;
  chartOptions?: ChartOption;
  seriesOptions?: Partial<SeriesData>;
  unit?: string;
  useFlex?: boolean;
  currentValue?: string | null;
  metricValueWidth?: number;
  tooltipDescription?: string;
}

export const setup = (props: Props) => {
  const { axisStyle, tooltipStyle } = useEvChartStyle();
  const defaultChartOptions: ChartOption = {
    type: 'line',
    width: '100%',
    height: '100%',
    padding: {
      top: 4,
      bottom: 4,
    },
    title: {
      show: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      ...tooltipStyle,
      use: true,
      formatter: props.tooltipDescription
        ? {
            html: (seriesList) => `
          <div class="ev-chart-tooltip-custom trend-chart-cell__tooltip">
            <div class="ev-chart-tooltip-custom__header">
              ${dayjs(seriesList[0]?.data?.x).format('HH:mm:ss')}
            </div>
            <div class="ev-chart-tooltip-custom__body">
              ${seriesList
                .map(
                  ({ name, data, color }) => `
                <div class="row" data-evui-tooltip-row>
                  <div class="series-name">
                    <span class="color-circle" style="background-color: ${color}; flex-shrink: 0;"></span>${name}
                  </div>
                  <div class="value">${data?.y != null ? Number(data.y).toFixed(3) : '-'}</div>
                </div>`,
                )
                .join('')}
            </div>
            <div class="trend-chart-cell__tooltip-desc">${props.tooltipDescription}</div>
          </div>
        `,
          }
        : {
            value: ({ y }: { y: number }) => (y != null ? y.toFixed(3) : '-'),
          },
    },
    axesX: [
      {
        ...axisStyle,
        type: 'time',
        showGrid: false,
        showAxis: false,
        labelStyle: {
          show: false,
        },
        formatter: (value) => dayjs(value).format('DD HH:mm:ss'),
      },
    ],
    axesY: [
      {
        ...axisStyle,
        type: 'linear',
        showGrid: false,
        showAxis: false,
        startToZero: true,
        autoScaleRatio: 0.1,
        labelStyle: {
          show: false,
        },
        range: props.unit === '%' ? [0, 100] : undefined,
        niceScale: props.unit !== '%',
      },
    ],
    indicator: {
      use: false,
    },
  };
  // 기본 시리즈 옵션
  const defaultSeriesOptions: SeriesData = {
    name: 'Metric',
    type: 'line',
    fill: true,
    // fill 토큰(blue-09-01)이 테마별 틴트를 이미 인코딩하므로 opacity로 흐리지 않는다
    fillColor: getCSSVariable('var(--color-blue-09-01)'),
    fillOpacity: 1,
    color: getCSSVariable('var(--color-blue-05-05)'),
    lineWidth: 2,
    point: false,
    pointSize: 0,
    pointHighlight: false,
  };

  const chartOption = computed<ChartOption>(() => {
    return {
      ...defaultChartOptions,
      ...props.chartOptions,
    };
  });

  const chartData = computed<ChartData>(() => {
    const { data } = props;

    if (
      !data ||
      typeof data !== 'object' ||
      !Array.isArray(data.times) ||
      !Array.isArray(data.values)
    ) {
      return {
        series: {},
        data: {},
        labels: [],
      } as ChartData;
    }
    const { times: labels, values } = data;

    const seriesConfig = {
      ...defaultSeriesOptions,
      ...props.seriesOptions,
    };

    return {
      series: {
        metric: seriesConfig,
      },
      data: {
        metric: values,
      },
      labels,
    };
  });

  const currentValue = computed(() => {
    if (props.currentValue !== undefined) {
      return props.currentValue;
    }

    const last = getTrendChartLastValue(props.data);
    return last == null ? null : last.toFixed(2);
  });

  const metricValueWidthPx = computed(() => `${props.metricValueWidth ?? 36}px`);

  return {
    chartOption,
    chartData,
    currentValue,
    metricValueWidthPx,
  };
};
